import { useEffect } from "react";
import { useSite } from "@/context/SiteContext";

const SITE = process.env.REACT_APP_SITE_URL || "https://finance.masharaskills.com";
const OG_IMAGE = `${SITE}/og-image.jpg`;

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function useSEO({ title: baseTitle, description: baseDescription, path = "/", canonicalPath, jsonLd, noindex = false, ogType = "website" }) {
  const { site } = useSite();
  const override = site?.seo?.[canonicalPath || path] || {};
  const title = override.title || baseTitle;
  const description = override.description || baseDescription;
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : "";
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta("name", "description", description);
      upsertMeta("property", "og:description", description);
      upsertMeta("name", "twitter:description", description);
    }
    if (title) {
      upsertMeta("property", "og:title", title);
      upsertMeta("name", "twitter:title", title);
    }
    const url = `${SITE}${path}`;
    const canonicalUrl = `${SITE}${canonicalPath || path}`;
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("name", "twitter:image", OG_IMAGE);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", noindex ? url : canonicalUrl);

    document.head.querySelectorAll("script#page-jsonld").forEach((s) => s.remove());
    let script;
    if (jsonLdKey) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = "page-jsonld";
      script.textContent = jsonLdKey;
      document.head.appendChild(script);
    }
    return () => {
      if (script) script.remove();
    };
  }, [title, description, path, canonicalPath, jsonLdKey, noindex, ogType]);
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE}${item.path}`,
    })),
  };
}

export const ORG_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE}/#organization`,
  name: "Mashara Finance",
  url: SITE,
  logo: OG_IMAGE,
  image: OG_IMAGE,
  description:
    "All-in-one business management platform for Finance, Accounting, HRMS, Payroll, Attendance, Partner Settlement, Candidate Management, Operations and Role-Based Access Control.",
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9162055572",
    contactType: "sales",
    areaServed: "IN",
    availableLanguage: ["English", "Hindi"],
  },
};

export const WEBSITE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE}/#website`,
  name: "Mashara Finance",
  url: `${SITE}/`,
  publisher: { "@id": `${SITE}/#organization` },
};

export const SOFTWARE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Mashara Finance",
  url: `${SITE}/`,
  image: OG_IMAGE,
  applicationCategory: "BusinessApplication",
  operatingSystem: "Web",
  offers: {
    "@type": "Offer",
    price: "999",
    priceCurrency: "INR",
    url: `${SITE}/pricing`,
    availability: "https://schema.org/InStock",
  },
  publisher: { "@id": `${SITE}/#organization` },
  description:
    "One platform to manage Finance, Accounting, HRMS, Payroll, Attendance, Expenses, Invoices and Business Operations for growing organizations.",
};
